// Customer wallet statement PDF: a bank-style ledger of every wallet balance
// change, newest first, from the closing balance down to the opening one.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fDateTime } from "src/utils/formatTime";
import { walletEntryType } from "./walletHistory.constant";

// Amik Design System blues, plus ledger credit/debit colours.
const BRAND = [20, 136, 214];
const BRAND_DARK = [11, 86, 137];
const BRAND_LIGHT = [227, 241, 251];
const INK = [33, 43, 54];
const MUTED = [110, 120, 130];
const RULE = [220, 226, 232];
const CREDIT = [46, 125, 50];
const DEBIT = [211, 47, 47];

// Current Amik brand mark (the one the app header shows). It is a vector, so it
// is rasterised at 4x for a crisp print and flattened onto white.
const LOGO_URL = "/logo/amikBrand.svg";
const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = 4;
        const w = (img.naturalWidth || 428) * scale;
        const h = (img.naturalHeight || 116) * scale;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: w,
          height: h,
        });
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = LOGO_URL;
  });

const MARGIN = 14;
const WEBSITE = "www.amiktech.in";
const CONTACT_EMAIL = "info@amiktech.in";
const dateFormat = "dd MMM yyyy, hh:mm a";

const toNumber = (value) => Number.parseFloat(value ?? 0) || 0;
// jsPDF's built-in fonts have no rupee sign, so amounts carry "INR" instead.
const money = (value) =>
  toNumber(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const describe = (row) => {
  const { label } = walletEntryType(row);
  const title = {
    "Top-up": "Wallet top-up",
    Charging: "Charging session",
    Manual: "Manual adjustment",
  }[label];
  return row.note ? `${title}\n${row.note}` : title;
};

const reference = (row) => {
  const parts = [];
  if (row.transaction_id) parts.push(`Session #${row.transaction_id}`);
  if (row.payment_id) parts.push(row.payment_id);
  return parts.join("\n") || "-";
};

// A titled, light-blue panel of label/value lines.
const drawPanel = (doc, { x, y, w, h, title, lines }) => {
  doc.setFillColor(...BRAND_LIGHT);
  doc.setDrawColor(...RULE);
  doc.roundedRect(x, y, w, h, 2, 2, "FD");
  doc.setFillColor(...BRAND);
  doc.rect(x, y + 2, 1.2, h - 4, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND_DARK);
  doc.text(title.toUpperCase(), x + 5, y + 7);

  let lineY = y + 13.5;
  lines.forEach(({ label, value, color, bold }) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    doc.text(label, x + 5, lineY);
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 10 : 9);
    doc.setTextColor(...(color || INK));
    const text = doc.splitTextToSize(String(value || "-"), w - 38)[0];
    doc.text(text, x + w - 5, lineY, { align: "right" });
    lineY += 6;
  });
};

/**
 * rows: wallet history rows as the API returns them (newest first).
 * customer: { id, firstName, lastName, email, phone }.
 */
export const downloadWalletStatementPdf = async ({
  rows,
  customer,
  customerId,
  brand = "Amik",
}) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const logo = await loadLogo();

  // Rows arrive newest first, which is the order the ledger prints in.
  const ledger = rows;
  const first = ledger[ledger.length - 1];
  const last = ledger[0];
  const opening =
    toNumber(first.current_balance) - toNumber(first.amount_changed);
  const closing = toNumber(last.current_balance);
  const credits = ledger.reduce(
    (sum, r) => sum + Math.max(toNumber(r.amount_changed), 0),
    0
  );
  const debits = ledger.reduce(
    (sum, r) => sum + Math.max(-toNumber(r.amount_changed), 0),
    0
  );
  const name =
    [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") || "-";
  const generatedAt = fDateTime(new Date(), dateFormat);
  const period = `${fDateTime(first.modify_time, "dd MMM yyyy")} - ${fDateTime(
    last.modify_time,
    "dd MMM yyyy"
  )}`;

  // ---- Header: logo left, statement title right, brand rule underneath.
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pageWidth, 3, "F");
  if (logo?.dataUrl) {
    const w = 42;
    const h = (logo.height / logo.width) * w;
    try {
      doc.addImage(logo.dataUrl, "PNG", MARGIN, 9, w, h);
    } catch (e) {
      /* logo optional */
    }
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...BRAND);
    doc.text(brand, MARGIN, 20);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...BRAND_DARK);
  doc.text("Wallet Statement", pageWidth - MARGIN, 17, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  doc.text(`Statement period: ${period}`, pageWidth - MARGIN, 23, {
    align: "right",
  });
  doc.text(`Generated on: ${generatedAt}`, pageWidth - MARGIN, 28, {
    align: "right",
  });
  // Contact details under the logo, level with the statement details.
  doc.setFontSize(8.5);
  doc.setTextColor(...BRAND_DARK);
  doc.textWithLink(WEBSITE, MARGIN, 28, { url: `https://${WEBSITE}` });
  const websiteWidth = doc.getTextWidth(WEBSITE);
  doc.setTextColor(...RULE);
  doc.text("|", MARGIN + websiteWidth + 2.5, 28);
  doc.setTextColor(...BRAND_DARK);
  doc.textWithLink(CONTACT_EMAIL, MARGIN + websiteWidth + 5.5, 28, {
    url: `mailto:${CONTACT_EMAIL}`,
  });
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, 35, pageWidth - MARGIN, 35);

  // ---- Customer and summary panels.
  const panelY = 41;
  const gap = 6;
  const panelW = (contentWidth - gap) / 2;
  const panelH = 38;
  drawPanel(doc, {
    x: MARGIN,
    y: panelY,
    w: panelW,
    h: panelH,
    title: "Customer details",
    lines: [
      { label: "Name", value: name, bold: true },
      { label: "Email", value: customer?.email },
      { label: "Phone", value: customer?.phone },
      { label: "Customer ID", value: customer?.id ?? customerId },
    ],
  });
  drawPanel(doc, {
    x: MARGIN + panelW + gap,
    y: panelY,
    w: panelW,
    h: panelH,
    title: "Account summary (INR)",
    lines: [
      { label: "Opening balance", value: money(opening) },
      { label: "Total credits", value: `+ ${money(credits)}`, color: CREDIT },
      { label: "Total debits", value: `- ${money(debits)}`, color: DEBIT },
      {
        label: "Closing balance",
        value: money(closing),
        color: closing < 0 ? DEBIT : BRAND_DARK,
        bold: true,
      },
    ],
  });

  // ---- Ledger.
  const balanceRow = (label, value) => [
    "",
    { content: label, styles: { fontStyle: "bold" } },
    "",
    "",
    "",
    { content: money(value), styles: { fontStyle: "bold" } },
  ];
  const body = [
    balanceRow("Closing balance", closing),
    ...ledger.map((row) => {
      const changed = toNumber(row.amount_changed);
      return [
        `${fDateTime(row.modify_time, "dd MMM yyyy")}\n${fDateTime(
          row.modify_time,
          "hh:mm a"
        )}`,
        describe(row),
        reference(row),
        changed > 0 ? money(changed) : "",
        changed < 0 ? money(-changed) : "",
        money(row.current_balance),
      ];
    }),
    balanceRow("Opening balance", opening),
  ];

  autoTable(doc, {
    startY: panelY + panelH + 8,
    margin: { left: MARGIN, right: MARGIN, top: 18, bottom: 20 },
    head: [["Date", "Description", "Reference", "Credit", "Debit", "Balance"]],
    body,
    foot: [
      [{ content: "Total", colSpan: 3 }, money(credits), money(debits), ""],
    ],
    showFoot: "lastPage",
    theme: "plain",
    styles: {
      font: "helvetica",
      fontSize: 8,
      textColor: INK,
      cellPadding: { top: 2.6, bottom: 2.6, left: 2.5, right: 2.5 },
      valign: "middle",
      overflow: "linebreak",
      lineColor: RULE,
    },
    headStyles: {
      lineWidth: 0,
      fillColor: BRAND_DARK,
      textColor: 255,
      fontStyle: "bold",
      fontSize: 8.5,
    },
    footStyles: {
      lineWidth: 0,
      fillColor: BRAND_LIGHT,
      textColor: BRAND_DARK,
      fontStyle: "bold",
      fontSize: 8.5,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 26 },
      1: { cellWidth: 50 },
      2: { cellWidth: "auto", textColor: MUTED, fontSize: 7.5 },
      3: { cellWidth: 21, halign: "right" },
      4: { cellWidth: 21, halign: "right" },
      5: { cellWidth: 24, halign: "right", fontStyle: "bold" },
    },
    didParseCell: (data) => {
      // Money columns right-aligned in the header and footer too.
      if (data.column.index >= 3 && data.section !== "body") {
        data.cell.styles.halign = "right";
      }
      if (data.section !== "body") return;
      if (data.column.index === 3 && data.cell.raw) {
        data.cell.styles.textColor = CREDIT;
      }
      if (data.column.index === 4 && data.cell.raw) {
        data.cell.styles.textColor = DEBIT;
      }
      if (
        data.column.index === 5 &&
        toNumber(
          String(data.cell.raw?.content ?? data.cell.raw).replace(/,/g, "")
        ) < 0
      ) {
        data.cell.styles.textColor = DEBIT;
      }
    },
    didDrawCell: (data) => {
      // Hairline under each body row instead of a full grid.
      if (data.section === "body") {
        doc.setDrawColor(...RULE);
        doc.setLineWidth(0.2);
        doc.line(
          data.cell.x,
          data.cell.y + data.cell.height,
          data.cell.x + data.cell.width,
          data.cell.y + data.cell.height
        );
      }
    },
    didDrawPage: (data) => {
      // Continuation pages get a compact running header.
      if (data.pageNumber > 1) {
        doc.setFillColor(...BRAND);
        doc.rect(0, 0, pageWidth, 3, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...BRAND_DARK);
        doc.text(`Wallet Statement - ${name}`, MARGIN, 11);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...MUTED);
        doc.text(period, pageWidth - MARGIN, 11, { align: "right" });
      }
    },
  });

  // ---- Footer on every page (needs the final page count).
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, pageHeight - 14, pageWidth - MARGIN, pageHeight - 14);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(
      "This is a computer-generated statement and does not require a signature. All amounts are in INR.",
      MARGIN,
      pageHeight - 9
    );
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - MARGIN, pageHeight - 9, {
      align: "right",
    });
  }

  const fileName = (name !== "-" ? name : `customer_${customerId}`)
    .trim()
    .replace(/[^\w-]+/g, "_");
  doc.save(`Wallet_Statement_${fileName}.pdf`);
};
